import { TFunction } from 'i18next';
import { z } from 'zod';

import { MembershipType } from '@/modules/team/types/membership-type';

/**
 * Validation schema for team member information form with i18n support.
 * Ensures data integrity for team member creation and updates.
 *
 * @param t - i18n translation function
 * @returns Zod schema with localized error messages
 */
export const teamMemberInfoSchema = (t: TFunction) => {
  return z
    .object({
      // Required fields
      name: z.string().min(1, t('common:error.required')),
      email: z.string().optional(),

      // Optional fields with specific validation
      avatar: z.instanceof(File).optional(),
      phoneNumber: z.string().optional(),
      dateOfJoin: z.date().optional(),

      // Boolean fields
      isPlaceholder: z.boolean().optional(),
      isManager: z.boolean().optional(),

      // Location fields - store both ID (for selection) and name (for submission)
      country: z.string().optional(), // Country code (e.g., "US")
      countryName: z.string().optional(), // Country name (e.g., "United States")
      city: z.string().optional(), // City geonameId (e.g., "5128581")
      cityName: z.string().optional(), // City name (e.g., "New York")

      // Selection fields
      tags: z.array(z.string()).optional(),
      membershipType: z
        .nativeEnum(MembershipType)
        .nullish()
        .transform(v => v ?? MembershipType.InHouse),
      department: z.string().optional(),
      role: z.string().optional(),
      seniority: z.string().optional(),
      managerId: z.string().optional(),

      // Text fields
      about: z.string().optional(),

      // Custom attributes — keyed by normalized attribute name.
      // Select types store option ID(s); other (unsupported) types are filtered out upstream.
      customAttributes: z.record(z.any()).optional().default({}),
    })
    .refine(
      data => {
        // Skip email validation when the member is a placeholder — the email field
        // is hidden in the UI, so any stale value left in form state must not block submit.
        if (data.isPlaceholder) {
          return true;
        }
        // If email is not provided, skip format validation
        if (!data.email || data.email.length === 0) {
          return true;
        }
        return z.string().email().safeParse(data.email).success;
      },
      {
        message: t('common:error.emailInvalid'),
        path: ['email'],
      }
    );
};

export type TeamMemberInfoFormData = z.infer<ReturnType<typeof teamMemberInfoSchema>>;
